import { useEffect, useState } from "react";
import { uploadInventorySnapshot } from "../services/inventoryService";
import { useAuth } from "../context/AuthContext";
import { getAllProducts } from "../services/dataCache";

export default function AdminInventory() {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Low-stock alert list (backlog, overnight 2026-07-11): active products at
  // or under the threshold, worst first. Threshold is a page-local setting.
  // Kept as a STRING so the field is freely editable (clearable, fractions
  // like 0.5 allowed) — parsed with parseFloat for the filter.
  const [products, setProducts] = useState([]);
  const [thresholdInput, setThresholdInput] = useState("10");
  const threshold = parseFloat(thresholdInput);
  const thresholdValid = !isNaN(threshold) && threshold >= 0;

  useEffect(() => {
    getAllProducts().then(setProducts); // session cache — 0 reads on revisit
  }, []);

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    setError("");
    setResult(null);
    try {
      const res = await uploadInventorySnapshot(file, user.uid);
      setResult(res);
      setProducts(await getAllProducts(true)); // stock just changed — refresh list
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  const lowStock = thresholdValid
    ? products
        .filter(p => p.active && p.stockQty <= threshold)
        .sort((a, b) => a.stockQty - b.stockQty)
    : [];

  return (
    <div className="admin-inventory">
      <h2>Daily Inventory Update</h2>
      <p className="inventory-help">
        Export your current inventory from QuickBooks as CSV UTF-8, then upload it here.
        This resets every product's stock count to match QuickBooks exactly — it will
        overwrite any changes made by web orders placed since your last upload.
      </p>
      <p className="inventory-help-note">
        Export from QuickBooks the same way you did for the original product list —
        Item List report, saved as <strong>CSV UTF-8 (Comma delimited)</strong>.
        No need to edit columns, the format is handled automatically.
      </p>

      <input
        type="file"
        accept=".csv"
        onChange={(e) => setFile(e.target.files[0])}
      />
      <button onClick={handleUpload} disabled={!file || uploading}>
        {uploading ? "Uploading..." : "Upload Inventory"}
      </button>

      {error && <p className="error">{error}</p>}
      {result && (
        <p className="inventory-success">
          Done — updated stock for {result.itemsUpdated} products.
          {result.newItemsCreated > 0 && (
            <> {result.newItemsCreated} new item{result.newItemsCreated !== 1 ? "s" : ""} detected and added to the catalog automatically.</>
          )}
          {result.barcodesImported > 0 && (
            <> Imported {result.barcodesImported} barcode{result.barcodesImported !== 1 ? "s" : ""} from the Barcode column.</>
          )}
        </p>
      )}

      <div className="low-stock-section">
        <h3>
          ⚠ Low stock — {lowStock.length} active product{lowStock.length !== 1 ? "s" : ""} at or under{" "}
          <input
            type="number"
            className="low-stock-threshold"
            min={0}
            step={0.25}
            value={thresholdInput}
            onChange={(e) => setThresholdInput(e.target.value)}
          />{" "}
          cases
        </h3>
        {!thresholdValid ? (
          <p className="inventory-help-note">Type a threshold number above (e.g. 10, or 0.5).</p>
        ) : lowStock.length === 0 ? (
          <p className="inventory-help-note">Nothing under the threshold — stock levels look healthy.</p>
        ) : (
          <table>
            <thead><tr><th>SKU</th><th>Product</th><th>Stock</th></tr></thead>
            <tbody>
              {lowStock.map(p => (
                <tr key={p.sku}>
                  <td>{p.sku}</td>
                  <td>{p.name}</td>
                  <td className={p.stockQty <= 0 ? "out-of-stock" : ""}>
                    {Math.ceil(p.stockQty * 100) / 100}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
